import React, { Component } from 'react';
import { Modal, Spin } from 'antd';
import { AiOutlineEdit } from 'react-icons/ai';
import { withRouter } from '../utils/withRouter';
import { UserContext } from '../UserContext';
import example from "../assets/images/example_sponsor.png";
import api from "../api";
import "./styles/Sponsorship.css";
import { motion } from "framer-motion";

class Sponsorship extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      sponsor_name: '',
      banner_url: null,
      selectedFile: null,
      preview: null,
      isUploading: false,
      originalSponsorName: '',
      originalBannerUrl: null,
    };
  }

  componentDidMount() {
    const user = this.context.user;
    if (!user || !user.id || !user.access_token) {
      this.setState({ loading: false });
      return;
    }
    fetch(`${api.main}api/agent/sponsor_banner?sagent_id=${user.id}&access_token=${user.access_token}`)
      .then(async (res) => {
        if (!res.ok) { const body = await res.json().catch(() => ({})); throw new Error(body?.error || `Failed to load banner (${res.status})`); }
        return res.json();
      })
      .then((data) => {
        const sponsor_name = data?.sponsor_name || '';
        const banner_url = data?.banner_url || null;
        this.setState({
          sponsor_name,
          banner_url,
          originalSponsorName: sponsor_name,
          originalBannerUrl: banner_url,
          loading: false,
        });
      })
      .catch((err) => {
        this.setState({ loading: false });
        Modal.error({ title: 'Error', content: err.message || 'Something went wrong. Please try again later.' });
      });
  }

  componentWillUnmount() {
    if (this.state.preview) URL.revokeObjectURL(this.state.preview);
  }

  resetFileInput = () => {
    if (this.fileInput) this.fileInput.value = "";
  };

  handleFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];

    if (this.state.preview) URL.revokeObjectURL(this.state.preview);

    this.setState({
      selectedFile: file || null,
      preview: file ? URL.createObjectURL(file) : null,
    });

    this.resetFileInput();
  };

  // NEW: cancel & revert to baseline
  cancelChanges = () => {
    if (this.state.preview) URL.revokeObjectURL(this.state.preview);
    this.setState((s) => ({
      sponsor_name: s.originalSponsorName || '',
      banner_url: s.originalBannerUrl || null,
      selectedFile: null,
      preview: null,
    }));
    this.resetFileInput();
  };

  uploadFile = async () => {
    const { selectedFile, sponsor_name } = this.state;
    const user = this.context.user;

    if (!sponsor_name || !selectedFile) {
      Modal.warning({
        title: 'Missing Information',
        content: 'Please provide BOTH a sponsor name and an image to update the banner.',
      });
      return;
    }

    try {
      this.setState({ isUploading: true });

      const formData = new FormData();
      formData.append('access_token', user.access_token);
      formData.append('file', selectedFile);
      formData.append('sponsor_name', sponsor_name);
      formData.append('sagent_id', user.id);

      const resp = await fetch(`${api.main}api/agent/sponsor_upload`, {
        method: 'POST',
        body: formData,
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.error || `Upload failed (${resp.status})`);
      }

      const data = await resp.json();
      const nextUrl = data.banner_url || data.url || null;

      // SUCCESS: update current AND baseline
      if (this.state.preview) URL.revokeObjectURL(this.state.preview);
      this.setState({
        banner_url: nextUrl,
        selectedFile: null,
        preview: null,
        isUploading: false,
        originalSponsorName: sponsor_name,
        originalBannerUrl: nextUrl,
      });
      this.resetFileInput();

      Modal.success({
        title: 'Success',
        content: 'Banner updated successfully.',
      });
    } catch (e) {
      if (this.state.preview) URL.revokeObjectURL(this.state.preview);
      this.setState({
        isUploading: false,
        preview: null,
        selectedFile: null,
      });
      this.resetFileInput();

      Modal.error({
        title: 'Upload Failed',
        content: e.message || 'Upload failed. Please try again.',
      });
    }
  };

  openFilePicker = () => {
    if (this.fileInput) this.fileInput.click();
  };

  render() {
    const {
      sponsor_name,
      banner_url,
      preview,
      loading,
      selectedFile,
      isUploading,
      originalSponsorName,
    } = this.state;
    const hasImage = Boolean(preview || banner_url);

    if (loading) {
      return (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Spin /> <span>Loading banner…</span>
        </div>
      );
    }

    // Require both to enable Save
    const canSave = Boolean(sponsor_name && selectedFile);

    // Enable Cancel only if something changed (name edited or a file picked)
    const hasPendingChanges = Boolean(preview || selectedFile || sponsor_name !== originalSponsorName);

    return (
      <div className="sponsorship-page">
        <div className="page-header">
          <div>
            <p className="page-eyebrow">Branding</p>
            <h2>Sponsorship Banner</h2>
            <p className="section-subtitle">
              Upload your partner banner and name to personalize the ISO experience.
            </p>
          </div>
        </div>

        <section className="page-section sponsorship-panel">
          <div className="sponsorship-grid">
            <div className="sponsorship-card">
              <div className="sponsorship-instructions">
                <h3>Upload Checklist</h3>
                <ul>
                  <li>Enter a sponsor name and upload a PNG banner.</li>
                  <li>Recommended dimensions: 600×200px or a similar ratio.</li>
                  <li>Changes are visible after you save.</li>
                </ul>
              </div>

              <div className="sponsorship-form">
                <label htmlFor="sponsor_name">Sponsor name</label>
                <input
                  type="text"
                  id="sponsor_name"
                  className="sponsor-input"
                  placeholder="Enter the sponsor name"
                  value={sponsor_name}
                  onChange={(e) => this.setState({ sponsor_name: e.target.value })}
                />

                <div className="sponsorship-upload-row">
                  <motion.button
                    type="button"
                    className="btn btn-primary sponsorship-upload-btn"
                    onClick={this.openFilePicker}
                    disabled={isUploading}
                    title={banner_url ? 'Replace banner image' : 'Upload banner image'}
                  >
                    <AiOutlineEdit /> {banner_url ? 'Replace Banner' : 'Upload Banner'}
                  </motion.button>
                  {selectedFile && (
                    <div className="sponsorship-file">
                      Selected file: <strong>{selectedFile.name}</strong>
                    </div>
                  )}
                </div>

                <div className="sponsorship-image">
                  {hasImage ? (
                    <img
                      className="sponsorship-image-preview"
                      src={preview || banner_url}
                      alt={sponsor_name || 'Sponsor banner'}
                    />
                  ) : (
                    <div className="sponsorship-image-placeholder">
                      <span>Please upload a banner image.</span>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/png"
                    onChange={this.handleFileSelect}
                    style={{ display: 'none' }}
                    ref={(el) => (this.fileInput = el)}
                  />
                </div>

                <div className="sponsorship-actions">
                  <motion.button
                    className="btn btn-primary"
                    onClick={this.uploadFile}
                    disabled={!canSave || isUploading}
                  >
                    Save Changes
                  </motion.button>
                  <motion.button
                    className="btn btn-secondary"
                    onClick={this.cancelChanges}
                    disabled={!hasPendingChanges || isUploading}
                  >
                    Cancel
                  </motion.button>
                </div>
              </div>
            </div>

            <div className="sponsorship-card sponsorship-example">
              <h3>Preview</h3>
              <p className="section-subtitle">
                Example of how the banner appears inside the portal.
              </p>
              <div className="sponsorship-example-frame">
                <img
                  src={example}
                  alt="Example of User View"
                  className="sponsorship-example-image"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
}

export default withRouter(Sponsorship);
